import React, { useState } from 'react';
import {
  FaSave,
  FaPlus,
  FaEdit,
  FaTrash,
  FaComments,
  FaStar,
  FaTimes,
  FaCheckCircle,
  FaExclamationCircle,
  FaQuoteLeft,
  FaUser,
  FaBuilding,
} from 'react-icons/fa';
import { useAdmin } from '../context/AdminContext';
import './SectionManager.css';
import './TestimonialsManager.css';

const TEXT_LIMIT = 250;

const TestimonialsManager = () => {
  const { data, addItem, updateItem, deleteItem } = useAdmin();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [form, setForm] = useState({
    name: '',
    role: '',
    text: '',
    rating: 5,
  });

  const testimonialsList = data?.testimonials || [];
  const textLen = (form.text || '').length;

  const notifySuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (textLen > TEXT_LIMIT) {
      setError(`Testimonial text cannot exceed ${TEXT_LIMIT} characters.`);
      return;
    }

    try {
      if (editId) {
        await updateItem('testimonials', editId, form);
        notifySuccess('Testimonial updated successfully!');
      } else {
        await addItem('testimonials', form);
        notifySuccess('New testimonial added successfully!');
      }
      resetForm();
    } catch (err) {
      console.error('Error saving testimonial:', err);
      setError('Failed to save testimonial. Please try again.');
    }
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name || '',
      role: item.role || '',
      text: (item.text || '').slice(0, TEXT_LIMIT),
      rating: Number(item.rating) || 5,
    });
    setEditId(item.id);
    setShowForm(true);
    setError('');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this client testimonial?')) {
      await deleteItem('testimonials', id);
      notifySuccess('Testimonial deleted successfully.');
    }
  };

  const resetForm = () => {
    setForm({ name: '', role: '', text: '', rating: 5 });
    setEditId(null);
    setShowForm(false);
    setError('');
  };

  return (
    <div className="sg-manager">
      {/* ═══════════════════════════════════════════
          PAGE HEADER
         ═══════════════════════════════════════════ */}
      <div className="sg-page-header">
        <div className="sg-ph-left">
          <div className="sg-ph-icon">
            <FaComments />
          </div>
          <div className="sg-ph-text">
            <h2>Testimonials Manager</h2>
            <p>Review and manage client feedback and endorsement testimonials</p>
          </div>
        </div>
        <div className="sg-ph-right">
          <button className="sg-btn-gold" onClick={() => setShowForm(!showForm)}>
            {showForm ? (
              <>
                <FaTimes /> Close Form
              </>
            ) : (
              <>
                <FaPlus /> Add Testimonial
              </>
            )}
          </button>
        </div>
      </div>

      {/* Action Alerts */}
      {successMsg && (
        <div className="sg-alert sg-alert-success">
          <FaCheckCircle /> {successMsg}
        </div>
      )}

      {error && (
        <div className="sg-alert sg-alert-error">
          <FaExclamationCircle /> {error}
        </div>
      )}

      {/* ═══════════════════════════════════════════
          FORM CONTAINER
         ═══════════════════════════════════════════ */}
      {showForm && (
        <div className="sg-form-card">
          <div className="sg-form-header">
            <div>
              <h3>{editId ? 'Modify Testimonial' : 'Register New Testimonial'}</h3>
              <p>Configure rating metrics and endorsement text for public showcase</p>
            </div>
            <button className="sg-form-close" onClick={resetForm}>
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="sg-form">
            <div className="sg-form-row-3">
              {/* Client Name */}
              <div className="sg-field">
                <label>
                  <FaUser className="tm-field-icon" style={{ marginRight: '6px', color: '#c9a227' }} /> Client Name <span className="req">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Rahul Sharma"
                  maxLength={100}
                  required
                />
              </div>

              {/* Role/Company */}
              <div className="sg-field">
                <label>
                  <FaBuilding className="tm-field-icon" style={{ marginRight: '6px', color: '#c9a227' }} /> Position / Company <span className="req">*</span>
                </label>
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  placeholder="e.g. Managing Director, ABC Corp"
                  maxLength={100}
                  required
                />
              </div>

              {/* Rating Selector */}
              <div className="sg-field">
                <label>Corporate Rating</label>
                <select
                  value={form.rating}
                  onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                  className="tm-rating-select"
                >
                  <option value={5}>⭐⭐⭐⭐⭐ 5 / 5 Rating</option>
                  <option value={4}>⭐⭐⭐⭐ 4 / 5 Rating</option>
                  <option value={3}>⭐⭐⭐ 3 / 5 Rating</option>
                  <option value={2}>⭐⭐ 2 / 5 Rating</option>
                  <option value={1}>⭐ 1 / 5 Rating</option>
                </select>
              </div>
            </div>

            {/* Testimonial Text */}
            <div className="sg-field">
              <label className="sg-label-flex">
                <span>
                  <FaQuoteLeft className="tm-field-icon" style={{ marginRight: '6px', color: '#c9a227' }} /> Endorsement Description <span className="req">*</span>
                </span>
                <span className={`sg-char-count ${textLen >= TEXT_LIMIT ? 'over' : ''}`}>
                  {textLen}/{TEXT_LIMIT} characters
                </span>
              </label>

              <textarea
                value={form.text}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= TEXT_LIMIT) {
                    setForm({ ...form, text: val });
                  }
                }}
                rows="4"
                maxLength={TEXT_LIMIT}
                placeholder="Write official client endorsement review here (max 250 characters)..."
                required
              />

              {textLen >= TEXT_LIMIT && (
                <small className="tm-limit-warning">
                  <FaExclamationCircle /> Maximum limit of {TEXT_LIMIT} characters reached.
                </small>
              )}
            </div>

            {/* Action buttons */}
            <div className="sg-form-actions">
              <button type="submit" className="sg-btn-gold">
                <FaSave /> {editId ? 'Update Testimonial' : 'Register Testimonial'}
              </button>
              <button type="button" className="sg-btn-outline" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          DATA VIEW (TABLE LIST)
         ═══════════════════════════════════════════ */}
      <div className="sg-table-card">
        <div className="sg-table-header">
          <div>
            <h3>All Registered Testimonials</h3>
            <p>Approve and edit client feedback visible on public homepage carousel</p>
          </div>
          <span className="sg-count-badge">{testimonialsList.length} Total</span>
        </div>

        <div className="sg-table-wrap">
          <table className="sg-table">
            <thead>
              <tr>
                <th style={{ width: '200px' }}>Client Name</th>
                <th style={{ width: '220px' }}>Role / Company</th>
                <th>Endorsement Preview</th>
                <th style={{ width: '150px' }}>Rating</th>
                <th style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {testimonialsList.length === 0 ? (
                <tr>
                  <td colSpan="5" className="sg-empty">
                    <div className="sg-empty-inner">
                      <FaComments />
                      <h4>No Testimonials Published</h4>
                      <p>Click "Add Testimonial" to register your first client review</p>
                    </div>
                  </td>
                </tr>
              ) : (
                testimonialsList.map((item) => {
                  const ratingVal = Math.max(1, Math.min(5, Number(item.rating) || 5));
                  return (
                    <tr key={item.id}>
                      <td>
                        <strong className="sg-row-title">{item.name || 'Anonymous'}</strong>
                      </td>
                      <td>
                        <span className="tm-role-text">{item.role || 'Partner'}</span>
                      </td>
                      <td>
                        <p className="tm-quote-preview">
                          "{(item.text || '').substring(0, 75)}
                          {(item.text || '').length > 75 ? '...' : ''}"
                        </p>
                      </td>
                      <td>
                        <div className="tm-stars-row">
                          {[...Array(ratingVal)].map((_, i) => (
                            <FaStar key={i} />
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="sg-actions">
                          <button
                            className="sg-action-btn sg-edit"
                            onClick={() => handleEdit(item)}
                            title="Edit Testimonial"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="sg-action-btn sg-delete"
                            onClick={() => handleDelete(item.id)}
                            title="Delete Testimonial"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsManager;